// packages/web/src/lib/cockpitSurfaces.ts — S1c (decision eb659c8d, Huan
// 2026-10-12; spec COCKPIT-ASSISTANT-SPEC.md §S1c): the SINGLE source of
// truth for which surfaces the title switcher offers on a pack.
//
// Pure and static on purpose. A later chunk ("Atlas can switch the screen
// when asked") validates a requested surface KEY against exactly this
// function's output, so every key here is stable, lowercase, and
// documented — never derived from an async `GET /api/team/me` destinations
// list that could answer differently from one request to the next. The
// switcher's COUNTS and attention dot still come from the app's existing
// badge state (App.tsx) — this file only answers "what doors exist and
// where do they go," never "what's behind them right now."
//
// Mirrors packages/verticals/personal/src/index.ts's own `navRoles.tabs`
// declaration (the pack's real screen list) — this is not a second,
// independently-invented list:
//   · 'threads' (labelled "Conversations" there) is deliberately NOT a
//     surface here. S1c retires it as a tab — Mail replaces Conversations
//     in the switcher — and its old route redirects to 'mail' (App.tsx's
//     redirect table) rather than disappearing or 404ing.
//   · 'calendar' IS listed even though the pack's `tabs` array has no bare
//     'calendar' entry (today it's reached only via the Personal sub-tab
//     strip): S1c's own instruction ("Calendar leaves Personal to be its
//     own surface") promotes it to a first-class switcher door. The screen
//     and its route already exist (routes/route.ts owns `calendar` for
//     'personal') — this adds a DOOR, not a new screen.
//   · 'settings' is likewise not a pack `tab` (Settings has never ridden
//     the primary rail on any pack) but the switcher needs a door to it
//     now that the rail carrying the gear icon is gone for this pack.
//   · 'clients' is this switcher's OWN key for the business-Accounts
//     screen — the underlying TopTab stays `buckets` (routes/route.ts);
//     `clients` as a TopTab key already belongs to Rexx's registry rail,
//     so reusing it here would be a second, incompatible meaning for one
//     string. The switcher's key is its own small namespace, independent
//     of TopTab, for exactly this reason.
//
// cockpitSurfaces.test.ts pins this list against the pack's real tabs (so
// the two can't silently drift) and asserts the key/shortcut/group
// invariants a validator would need.

import { routePath, type PackVertical } from '../routes/route';

export type CockpitSurfaceGroup = 'primary' | 'surfaces' | 'settings';

export interface CockpitSurface {
  /** Stable, lowercase, documented. Once shipped, a key is never renamed
   *  or reused for a different surface — see the file header. */
  key: string;
  label: string;
  /** A hash route ("#/…"), ready to use as an <a href> or passed straight
   *  to the shell's `navigate`. */
  route: string;
  /** Which divider-separated group of the switcher menu this surface sits
   *  in. Mirrors mockup-v10's own three groups exactly: a divider after
   *  Calendar, a second divider before Settings — not after Personal, as
   *  a looser reading of the brief's prose might suggest. */
  group: CockpitSurfaceGroup;
  /** The laptop/Fold-open keyboard shortcut (⌘1…⌘9 / Ctrl+1…9), FIXED to
   *  the surface rather than to its position in the (possibly most-used-
   *  first reordered) menu list, so the binding keeps its muscle memory
   *  even as the list above it reorders. Undefined past the 9-key budget
   *  — Radar, Record and Settings carry none, same as the mockup, which
   *  only ever decorates two of the first three with a visible ⌘ badge
   *  (Mail, Calendar) despite every surface in this group being bindable. */
  shortcut?: number;
}

/**
 * The switcher's surface list for a pack. Every other pack returns an
 * empty list — S1c is the personal pack's switcher only, and an empty
 * list is how a non-cockpit build says "no switcher surfaces exist," the
 * same convention `defaultRailFor` already uses for "this pack declares
 * nothing."
 */
export function cockpitSurfaces(pack: PackVertical): CockpitSurface[] {
  if (pack !== 'personal') return [];
  return [
    // H1a (COCKPIT-HOME-COLUMNS-SPEC.md §H1) — the board is the pack's
    // front page now; Home is the switcher's FIRST door (the brief's own
    // instruction). No ⌘ shortcut: the laptop budget (⌘1…⌘9) was already
    // fully claimed by the eight surfaces below before this chunk, and the
    // spec marks a surface's shortcut as fixed forever once shipped — Home
    // arrives after that commitment, so it ships without one rather than
    // reassigning an existing binding (reported as a judgement call).
    { key: 'home', label: 'Home', route: routePath({ screen: 'home' }), group: 'primary' },
    // `routePath({screen:'today'})` returns '/' (the bare default every
    // OTHER pack still lands on) — a literal here, not that call, because
    // on the personal pack '/' now redirects to Home (App.tsx); Today's
    // own door has to be the address that survives the redirect.
    { key: 'today', label: 'Today', route: '/today', group: 'primary', shortcut: 1 },
    { key: 'mail', label: 'Mail', route: routePath({ screen: 'mail' }), group: 'primary', shortcut: 2 },
    { key: 'calendar', label: 'Calendar', route: routePath({ screen: 'calendar' }), group: 'primary', shortcut: 3 },
    {
      key: 'personal',
      label: 'Personal',
      route: routePath({ screen: 'personal', sub: 'calendar' }),
      group: 'surfaces',
      shortcut: 4,
    },
    { key: 'review', label: 'Review', route: routePath({ screen: 'review' }), group: 'surfaces', shortcut: 5 },
    {
      key: 'audience',
      label: 'Audience',
      route: routePath({ screen: 'audience', sub: 'home' }),
      group: 'surfaces',
      shortcut: 6,
    },
    { key: 'clients', label: 'Clients', route: routePath({ screen: 'buckets' }), group: 'surfaces', shortcut: 7 },
    { key: 'delivery', label: 'Delivery', route: routePath({ screen: 'delivery' }), group: 'surfaces', shortcut: 8 },
    { key: 'systems', label: 'Systems', route: routePath({ screen: 'systems' }), group: 'surfaces', shortcut: 9 },
    { key: 'radar', label: 'Radar', route: routePath({ screen: 'radar' }), group: 'surfaces' },
    { key: 'record', label: 'Record', route: routePath({ screen: 'record' }), group: 'surfaces' },
    { key: 'settings', label: 'Settings', route: routePath({ screen: 'settings' }), group: 'settings' },
  ];
}

/** The surface keys that carry a live count badge in today's switcher,
 *  mapped to the NavDestination `tab` whose badge already exists at the
 *  shell level (App.tsx `navProps.badges`) — review's pending-proposal
 *  count and audience's unanswered count. Every surface NOT in this map
 *  shows no count, deliberately: S1c's own rule is "use counts the app
 *  already computes… do not invent new ones," and today/mail/calendar/
 *  personal/clients/delivery/systems/radar/record/settings have no such
 *  count computed anywhere in the app yet. mockup-v10 draws sample counts
 *  on Today/Mail/Radar too, but those are the mockup's placeholder data,
 *  not a real computed number — see the S1c brief report's judgement-call
 *  list. */
export const COCKPIT_SURFACE_BADGE_TAB: Readonly<Record<string, 'review' | 'audience'>> = {
  review: 'review',
  audience: 'audience',
};
